/**
 * CandidateFilters — search bar, status dropdown, sort controls, Upload Resume CTA.
 *
 * Props (exact interface):
 *   filters        { search: string }   current filter state
 *   onFilterChange (newFilters) => void  update parent filter state
 *   onUpload       () => void            open the add-candidate / resume modal
 *
 * Display-only helpers (passed from CandidatesPage):
 *   filtered         number   — count of candidates currently visible
 *   total            number   — total candidates in the list
 *   hasActiveFilters boolean
 *   onClearFilters   () => void
 *   isSourceRunning  boolean
 *   sourcingStatus   string
 *   onSourcingClick  () => void
 */
import { Search, X, Upload, Loader2, List, LayoutGrid } from 'lucide-react';

export function CandidateFilters({
  filters,
  onFilterChange,
  onUpload,
  filtered,
  total,
  hasActiveFilters,
  onClearFilters,
  isSourceRunning,
  sourcingStatus,
  onSourcingClick,
  viewMode,
  onViewModeChange,
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 mt-4"
      data-testid="candidate-filters"
    >
      {/* ── Search ───────────────────────────────────────────────── */}
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search all candidates..."
          value={filters.search}
          onChange={e => onFilterChange({ ...filters, search: e.target.value })}
          data-testid="candidate-search-input"
          className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2
                     text-sm text-slate-700 placeholder-slate-400
                     focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none
                     hover:bg-white/80 transition-all duration-200"
        />
      </div>

      {/* ── Result count badge ───────────────────────────────────── */}
      {filtered !== total && (
        <span className="text-xs text-slate-500 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
          <strong>{filtered}</strong> of {total} shown
        </span>
      )}

      {/* ── Clear all filters ────────────────────────────────────── */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="flex items-center gap-1 text-xs text-[#2563eb] hover:underline"
          data-testid="clear-filters-link"
        >
          <X className="w-3 h-3" /> Clear all filters
        </button>
      )}

      {/* ── View mode toggle ─────────────────────────────────────── */}
      {onViewModeChange && (
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => onViewModeChange('list')}
            className={`p-2 rounded-md flex items-center transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            title="List View"
            data-testid="view-toggle-list"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewModeChange('board')}
            className={`p-2 rounded-md flex items-center transition-all ${viewMode === 'board' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
            title="Board View"
            data-testid="view-toggle-board"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Upload Resume ────────────────────────────────────────── */}
      <button
        onClick={onUpload}
        data-testid="upload-resume-filter-btn"
        className="flex items-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white
                   border border-slate-200/50 hover:border-blue-300/50
                   text-slate-700 text-sm font-medium px-4 py-2 rounded-xl
                   shadow-sm transition-all duration-200"
      >
        <Upload className="w-4 h-4" /> Upload Resume
      </button>

      {/* ── Sourcing progress pill ───────────────────────────────── */}
      {isSourceRunning && (
        <button
          onClick={onSourcingClick}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200
                     rounded-xl text-xs font-medium ml-auto"
          data-testid="sourcing-progress-pill"
        >
          <Loader2 className="w-3.5 h-3.5 text-[#2563eb] animate-spin flex-shrink-0" />
          <p className="text-blue-500 animate-pulse">{sourcingStatus || 'Sourcing...'}</p>
        </button>
      )}
    </div>
  );
}
